// Pure transforms: Realtime server payloads → the shapes the Legend components already
// expect (the field names from the prototype's mock, so the UI needs no changes).
//
// NOTE: the trade/order document field names below are mapped defensively with fallbacks;
// they are confirmed against the live /api/trades schema in P4 and tightened then.

/**
 * Entry verbs whose order rests ABOVE the market. The chip's `kind` is geometry,
 * so this set is the whole of what decides 'break' vs 'dip' — and BUY_ON_BREAK
 * alone is only half of it now that shorts exist.
 */
const ABOVE_MARKET_ENTRY = new Set(['BUY_ON_BREAK', 'SHORT_ON_POP']);

/**
 * The label for an entry chip, read through the side.
 *
 * `kind` is GEOMETRY — 'break' means the order rests above the market, 'dip'
 * below — so the same geometry is a different word depending on which way the
 * trade points. Shared because it has to be: it lived inline in OrderOverlay,
 * and FloatingCard rendered the same field side-blind, so a resting short above
 * the market read 'POP' on the chart and green 'BRK' on the card at once.
 */
export function entryChipLabel(kind: string, side: string): 'DIP' | 'BRK' | 'BRKDN' | 'POP' {
    const below = kind === 'dip';
    return side === 'short' ? (below ? 'BRKDN' : 'POP') : (below ? 'DIP' : 'BRK');
}

// ---- symbols: /api/symbols/active|search element → Legend META-ish row ----
export function mapSymbol(s: any) {
  const pct = typeof s.priceChangePercent === 'number' ? s.priceChangePercent / 100 : 0; // server % → fraction
  const last = s.currentPrice ?? s.price ?? 0;
  const prevClose = s.priceChange != null ? last - s.priceChange : last;
  return {
    ticker: s.symbol,
    name: s.name || s.companyName || s.symbol,
    base: last, // seeded price; live price arrives via quotes
    last,
    prevClose,
    chg: pct,
    vol: (s.avgVolume ?? 0) / 1e6, // legend shows volume in millions
    rvol: s.rvol ?? 0, // no server field today
    logo: s.logo ?? null,
    // config passthrough (drives the per-symbol panel)
    targetPreset: s.targetPreset,
    runnerModeEnabled: s.runnerModeEnabled,
    automatedSignalsEnabled: s.automatedSignalsEnabled,
    accountSize: s.accountSize,
    riskPerTradePercent: s.riskPerTradePercent,
    maxPositionValuePercent: s.maxPositionValuePercent,
  };
}

// ---- bars: server bar (ISO timestamp) → lightweight-charts candle (unix seconds) ----
export function mapBar(b: any) {
  const time = Math.floor(new Date(b.timestamp).getTime() / 1000);
  const open = +b.open;
  const close = +b.close;
  return {
    time,
    open,
    high: +b.high,
    low: +b.low,
    close,
    value: +b.volume || 0,
    vwap: b.vwap != null ? +b.vwap : undefined,
    up: close >= open,
  };
}

// lightweight-charts requires a STRICTLY time-ascending, unique series and hard
// -crashes ("Value is null" on every repaint) otherwise. The bars endpoint can
// return duplicate timestamps on aggregated timeframes (observed: 1H ~377 dupes,
// 4H ~147) and, defensively, a bar could carry a non-finite value. So drop
// malformed bars, collapse duplicate timestamps (last write wins), and sort by
// time. Root cause is server-side (duplicate 1H/4H bars) but the chart must
// never crash on the data it's given.
const finiteBar = (b: any) =>
  Number.isFinite(b.time) && Number.isFinite(b.open) && Number.isFinite(b.high)
  && Number.isFinite(b.low) && Number.isFinite(b.close);

export const mapBars = (arr: any[]) => {
  const byTime = new Map<number, any>();
  for (const raw of arr || []) {
    const b = mapBar(raw);
    if (finiteBar(b)) byTime.set(b.time, b);
  }
  return Array.from(byTime.values()).sort((a, b) => a.time - b.time);
};

// ---- indicators: history snapshot → {time, values} (handles nested or flat shape) ----
export function mapIndicatorSnapshot(snap: any) {
  const time = Math.floor(new Date(snap.timestamp || snap.time || snap.t).getTime() / 1000);
  const v = snap.values || snap;
  return {
    time,
    values: {
      vwap: v.vwap, ema9: v.ema9, ema21: v.ema21, ema50: v.ema50, ema200: v.ema200,
      sma20: v.sma20, sma50: v.sma50, atr: v.atr,
    },
  };
}

// ---- S/R levels: flat per-type levels (from /levels) → rail confluence levels ----
// Clusters nearby prices into one rail level, unioning their timeframes and summing
// touches/trades; strength = max. Matches sr.jsx's { id, px, touches, timeframes[],
// trades, strength, lastTested } shape.
export function mapLevels(levels: any[], opts: { clusterPct?: number } = {}) {
  const clusterPct = opts.clusterPct ?? 0.004; // 0.4% price band
  const sorted = [...(levels || [])].sort((a, b) => b.px - a.px);
  const clusters: any[] = [];
  for (const l of sorted) {
    const anchor = clusters[clusters.length - 1];
    if (anchor && Math.abs(anchor.px - l.px) / anchor.px <= clusterPct) {
      anchor._members.push(l);
      anchor.touches += l.touches || 0;
      anchor.trades += l.trades || 0;
      anchor.strength = Math.max(anchor.strength, l.strength || 0);
      if (l.timeframe && !anchor.timeframes.includes(l.timeframe)) anchor.timeframes.push(l.timeframe);
      anchor._lastTestedAt = Math.max(anchor._lastTestedAt ?? 0, l.lastTestedAt ?? 0);
    } else {
      clusters.push({
        id: l.id,
        px: l.px,
        touches: l.touches || 0,
        trades: l.trades || 0,
        strength: l.strength || 0,
        timeframes: l.timeframe ? [l.timeframe] : [],
        _lastTestedAt: l.lastTestedAt ?? 0,
        _members: [l],
      });
    }
  }
  return clusters.map((c) => ({
    id: c.id,
    px: c.px,
    touches: c.touches,
    trades: c.trades,
    strength: c.strength,
    timeframes: c.timeframes,
    lastTested: relativeTime(c._lastTestedAt),
  }));
}

// ---- trades: /api/trades doc (+ orders[]) → Legend position model ----
// TODO(P4): confirm exact field names (avgEntryPrice, order.purpose, targetIndex) against
// the Mongo trade/order schema; these fallbacks cover the documented shapes (canvas.md §6.2).
export function mapTradeToPosition(trade: any) {
  if (!trade) return null;
  const pos = trade.position || {};
  const side = (pos.side || trade.side || 'LONG').toString().toLowerCase() === 'short' ? 'short' : 'long';
  const qty = pos.remainingQty ?? pos.qty ?? trade.remainingQty ?? 0;
  const avg = pos.avgEntryPrice ?? pos.avgPrice ?? pos.entryPrice ?? trade.avgEntryPrice ?? 0;

  const orders: any[] = trade.orders || [];
  const isActive = (o: any) => ['new', 'accepted', 'pending_new', 'partially_filled'].includes(o.status);

  // Target legs are written by the worker with purpose 'take_profit' (Alpaca
  // bracket take-profit). Accept 'target' too as a defensive fallback.
  const targets = orders
    .filter((o) => (o.purpose === 'take_profit' || o.purpose === 'target') && isActive(o))
    .sort((a, b) => (a.targetIndex ?? 0) - (b.targetIndex ?? 0))
    .map((o, i) => ({
      id: o.orderId,
      t: 'T' + ((o.targetIndex != null ? o.targetIndex + 1 : i + 1)),
      px: o.limitPrice,
      qty: (o.quantity ?? 0) - (o.filledQty ?? 0) || o.quantity || 0,
    }));

  // Entry chips: prefer the trade doc's embedded entryOrders[] (written by the
  // worker in lockstep with the engine — including synthetic sig- break orders
  // that never exist at the broker) over the attached orders collection, whose
  // synthetic rows self-tombstone on reprice (same-id replace → status
  // 'replaced' → hidden by the API's active-only filter) and never carry a
  // kind. Reprices APPEND rows with the same orderId (replaced→new pairs), so
  // the LAST row per id is the current state of that order.
  const entryRows: any[] = Array.isArray(trade.entryOrders) ? trade.entryOrders : [];
  let entries: any[];
  if (entryRows.length) {
    // Group by clientOrderId lineage (orderId fallback): broker replaces keep
    // the ORIGINAL clientOrderId on the replacement row, so lineage-keying
    // collapses replace pairs even when the old row's tombstone write was
    // missed (observed: oldOrder lookup misses under async timing and both
    // rows would otherwise stay 'new' under different orderIds → two chips).
    // Synthetic reprices reuse the same id for both fields — same group either
    // way. Array order is chronological ($push), so the LAST row is current.
    const lineage = (r: any) => r.clientOrderId || r.orderId;
    const lastById = new Map<string, any>();
    const filledIds = new Set<string>();
    for (const r of entryRows) {
      if (!r || !r.orderId) continue;
      lastById.set(lineage(r), r);
      // Fill-beats-append race (#557 class): a marketable fill can retro-push
      // its 'filled' row BEFORE the pending append lands, so the group reads
      // [filled, new] and last-row-wins would resurrect a phantom pending chip
      // for an already-filled order — with no later event to ever clear it.
      // A 'filled' row anywhere in the group means the group filled. Reprice
      // groups ([replaced→new]) contain no filled row, so drags keep
      // last-row-wins semantics.
      if (r.status === 'filled') filledIds.add(lineage(r));
    }
    // Best-effort conversion guard: if a fired synthetic's real broker order
    // happens to carry the sig- id as its clientOrderId, suppress the armed
    // chip. (Conversion also cancels the sig- rows transactionally engine-side;
    // this only covers the race where that write hasn't landed yet, and the
    // fire-time client id usually re-hashes — so it rarely matches. Harmless.)
    const converted = new Set(
      Array.from(lastById.values())
        .filter((r) => r.clientOrderId && r.clientOrderId !== r.orderId)
        .map((r) => r.clientOrderId),
    );
    // Liveness gate: a pending row whose last write is >24h old is treated as
    // a dead engine's leftover (orphaned ENTRY_PENDING docs — pod died mid-arm
    // and nothing terminal-ized the row; observed live: TVRD 26d,
    // GCTK/ADGM/VGAS 1d+). Trade docs carry no reliable heartbeat
    // (lastHeartbeat ≈ createdAt), so row submittedAt is the only clock until
    // the orphan reaper ships. KNOWN TRADEOFF: a #559-surviving arm older than
    // 24h (no EOD sweep exists for synthetics) would have its chip hidden while
    // the engine keeps the trigger — display-only, engine unaffected, and pods
    // recycle ~daily so >24h live arms are not seen in practice. The reaper is
    // the real fix for both sides of this ambiguity.
    const STALE_MS = 24 * 3600 * 1000;
    const fresh = (r: any) => {
      if (!r.submittedAt && !r.updatedAt) return true; // no clock → never hide a real order
      const t = new Date(r.submittedAt || r.updatedAt).getTime();
      return !Number.isFinite(t) || Date.now() - t < STALE_MS;
    };
    entries = Array.from(lastById.values())
      .filter((r) => isActive(r) && !filledIds.has(lineage(r)) && !converted.has(r.orderId) && fresh(r))
      .map((r) => ({
        id: r.orderId,
        // GEOMETRY, not verb: 'break' means the order rests ABOVE the market and
        // 'dip' BELOW, which is why OrderOverlay reads it through the side. Two
        // verbs rest above — BUY_ON_BREAK and SHORT_ON_POP — so testing only for
        // the first collapsed a resting short above the market onto 'dip' and
        // labelled it BRKDN: the word for the exact opposite intent, on the chip
        // the operator drags. SHORT_ON_DIP was right only by accident.
        // pre-#560 legacy rows lack commandType → keep today's 'dip' default
        kind: ABOVE_MARKET_ENTRY.has(String(r.commandType)) ? 'break' : 'dip',
        px: r.limitPrice,
        shares: r.quantity ?? 0,
        pending: r.status !== 'filled',
      }));
  } else {
    // Legacy trade docs with no entryOrders[] — original orders-collection path.
    entries = orders
      .filter((o) => (o.purpose === 'entry') && isActive(o))
      .map((o) => ({
        id: o.orderId,
        kind: ABOVE_MARKET_ENTRY.has(String(o.commandType)) ? 'break' : (o.kind || 'dip'),
        px: o.limitPrice,
        shares: o.quantity ?? 0,
        pending: o.status !== 'filled',
      }));
  }

  // The stop always protects the full remaining position. Track the live position
  // remainingQty (`qty`) rather than trade.stopLoss.remainingQty, which goes stale
  // after partial TP fills / trims until the worker amends the stop order — so the
  // stop qty + bubble risk update on every fill, mirroring the position/PnL bubble.
  const stop = trade.stopLoss && trade.stopLoss.level != null && trade.stopLoss.status !== 'triggered'
    ? { id: 'stop-' + (trade.tradeId || ''), px: trade.stopLoss.level, qty }
    : null;

  const last = pos.currentPrice ?? null;
  const unrealizedPnL = pos.unrealizedPnL ?? null;
  return { side, qty, avg, targets, stop, entries, last, unrealizedPnL };
}

// ---- quotes ----
export const quoteMid = (q: { bid?: number; ask?: number }) =>
  q.bid != null && q.ask != null ? (q.bid + q.ask) / 2 : (q.bid ?? q.ask ?? null);

// ---- helpers ----
function relativeTime(ms: number): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return 'now';
  const m = Math.floor(diff / 60000);
  if (m < 60) return m + 'm';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h';
  const d = Math.floor(h / 24);
  return d + 'd';
}

